import {ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent} from "@/components/ui/chart";
import clsxm from "@/lib/clsxm";
import type {ComponentPropsWithoutRef, FC} from "react";
import {Bar, BarChart, CartesianGrid, LabelList, XAxis} from "recharts";

const AppBarChart: FC<{
  barChartProps?: ComponentPropsWithoutRef<typeof BarChart>;
  chartTitle: string;
  chartData: {label?: string; value: number}[];
  className?: string;
  formatLabel?: (label: string) => string | undefined;
  shouldTruncate?: boolean;
  variant?: "ranking" | "percentage";
  chartConfig?: ChartConfig;
}> = ({
  barChartProps,
  chartTitle,
  chartData,
  className = "",
  formatLabel,
  shouldTruncate = true,
  variant = "percentage",
  chartConfig = {},
}) => {
  const data: {label?: string; value: number; ranking?: number}[] =
    variant === "ranking" ? chartData.map((data, index) => ({...data, ranking: index + 1})) : chartData;

  const formatter = (value: unknown) => {
    if (value === undefined || value === null) return "";
    const numValue = typeof value === "number" ? value : Number(value);

    const formattedValue =
      variant === "ranking"
        ? `${data.find(d => d.value === numValue)?.ranking}.`
        : `${Math.round(numValue * 100) / 100}%`;

    return formattedValue?.toString() ?? "";
  };

  return (
    <div className={clsxm("flex min-h-fit w-full flex-col justify-start gap-2.5", className)}>
      <span className="text-neutral-black text-sm font-normal">{chartTitle}</span>
      <ChartContainer config={chartConfig} className="min-h-50 w-full">
        <BarChart
          {...barChartProps}
          maxBarSize={45}
          data={data}
          accessibilityLayer
          layout="horizontal"
          margin={{top: 20}}>
          <CartesianGrid vertical={false} />
          <XAxis
            interval={0}
            tickLine={false}
            tickFormatter={value => formatLabel?.(value) ?? (shouldTruncate ? value.slice(0, 3) : value)}
            axisLine={false}
            dataKey="label"
          />
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent formatter={formatter} valueClassName="text-primary-dark-solid" />}
          />
          <Bar radius={4} dataKey="value" fill="#4D64FC">
            <LabelList position="top" offset={8} className="fill-foreground" fontSize={12} formatter={formatter} />
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
};

export default AppBarChart;
